import { ScientificAnalysisConditionComponentFactory, scientificAnalysisConditionComponentFactory } from '@/modules/scientific-analysis-module/components/scientific-analysis-filter-panel/scientific-analysis-filter-panel-conditionfactory'
import { initialScientificAnalysisFilterPanelState, ScientificAnalysisFilterPanelState } from '@/modules/scientific-analysis-module/components/scientific-analysis-filter-panel/scientific-analysis-filter-panel.models'
import { ScientificAnalysisSelectedFilter } from '@/modules/scientific-analysis-module/scientific-analysis-module.models'
import { ScientificAnalysisConditionTypeDTO, ScientificAnalysisComparatorType, ScientificAnalysisFieldDTO, ScientificAnalysisFilterEntityType, ScientificAnalysisSearchEntityType, ScientificAnalysisConditionTypeEnum } from '@/modules/scientific-analysis-module/services/scientific-analysis-dtos'
import { loadedFilterEntityTypes, ScientificAnalysisService, scientificAnalysisService } from '@/modules/scientific-analysis-module/services/scientific-analysis-services'
import { ScientificAnalysisComparisonType, ScientificAnalysisCondition, ScientificAnalysisConditionFieldGroup, ScientificAnalysisConditionValidationErrorCode } from '@/modules/scientific-analysis-module/shared/scientific-analysis.models'
import { Vue, Component, Prop, Emit, Watch, Ref } from 'vue-property-decorator'
import ScientificAnalysisFilterPanelFreetext from '@/modules/scientific-analysis-module/components/scientific-analysis-filter-panel-freetext/scientific-analysis-filter-panel-freetext.vue'
import ScientificAnalysisFilterPanelBoolean from '@/modules/scientific-analysis-module/components/scientific-analysis-filter-panel-boolean/scientific-analysis-filter-panel-boolean.vue'
import ScientificAnalysisFilterPanelRange from '@/modules/scientific-analysis-module/components/scientific-analysis-filter-panel-range/scientific-analysis-filter-panel-range.vue'
import ScientificAnalysisFilterPanelSelectentity from '@/modules/scientific-analysis-module/components/scientific-analysis-filter-panel-selectentity/scientific-analysis-filter-panel-selectentity.vue'
import ScientificAnalysisFilterPanelSelectpatientidentifier from '@/modules/scientific-analysis-module/components/scientific-analysis-filter-panel-selectpatientidentifier/scientific-analysis-filter-panel-selectpatientidentifier.vue'
import { Formatter } from '@/filters/formatter'
import ScientificAnalysisFilterPanelPatientage from '@/modules/scientific-analysis-module/components/scientific-analysis-filter-panel-patientage/scientific-analysis-filter-panel-patientage.vue'

@Component({
    components: {
        ScientificAnalysisFilterPanelFreetext,
        ScientificAnalysisFilterPanelBoolean,
        ScientificAnalysisFilterPanelRange,
        ScientificAnalysisFilterPanelSelectpatientidentifier,
        ScientificAnalysisFilterPanelSelectentity,
        ScientificAnalysisFilterPanelPatientage
    }
})
export default class ScientificAnalysisFilterPanel extends Vue {
    @Ref('selectedFilterInput') readonly selectedFilterInput!: any
    @Prop() selectedFilter!: ScientificAnalysisSelectedFilter
    state: ScientificAnalysisFilterPanelState
    scientificAnalysisService: ScientificAnalysisService = scientificAnalysisService
    componentFactoryService: ScientificAnalysisConditionComponentFactory = scientificAnalysisConditionComponentFactory

    constructor() {
        super()
        this.state = initialScientificAnalysisFilterPanelState()
    }

    mounted():void {
        this.focus()
    }

    public focus(): void {
        if (this.selectedFilterInput && this.selectedFilterInput.focus) {
            this.selectedFilterInput.focus()
        }
    }

    @Watch('selectedFilter')
    async onSelectedFilterChange(): Promise<void> {
        this.state.filterFieldText = ''
        await this.updateFieldList()
    }

    public created(): void {
        this.updateFieldList()
    }

    getComponent(condition: ScientificAnalysisCondition): string {
        const componentName = this.componentFactoryService.getComponent(condition.comparisonType)
        if (!componentName) {
            return 'div'
        }
        return componentName
    }

    _showComparisonSelection(condition: ScientificAnalysisCondition):boolean {
        return condition.comparisonType.conditionType !== ScientificAnalysisConditionTypeEnum.BOOLEAN
    }

    _onSelect(selectedComparison: ScientificAnalysisComparisonType, condition: ScientificAnalysisCondition): void {
        if (selectedComparison.comparatorType !== condition.comparisonType.comparatorType) {
            condition.value = ''
            condition.valueTo = ''
            condition.validationErrorCode = ScientificAnalysisConditionValidationErrorCode.NONE
        }
        this._onFilterModification()
    }

    private async updateFieldList(): Promise<void> {
        this.state.entityFields = loadedFilterEntityTypes[this.selectedFilter.entityFilters.entityType]?.metadataFieldFilterConditionsResponses ?? []
    }

    get filteredEntityFields(): ScientificAnalysisFieldDTO[] {
        const searchText = Formatter.removeAccents(this.state.filterFieldText.toLowerCase())
        const filtered = this.state.entityFields.filter(ef => Formatter.removeAccents(ef.translatedFieldName.toLowerCase()).includes(searchText))
        return filtered.sort((ef1, ef2) => ef1.translatedFieldName.localeCompare(ef2.translatedFieldName))
    }

    getLocalizedEntityName(
        entity:
            | ScientificAnalysisSearchEntityType
            | ScientificAnalysisFilterEntityType
    ): string {
        return this.$t(`scientificAnalysis.entity.${entity}`) as string
    }

    @Emit('removeFilterEvent')
    removeFilter(): void {
    }

    onFieldSelected(field: ScientificAnalysisFieldDTO): void {
        let fieldGroup = this.selectedFilter.filter.fieldGroups.find(fg => fg.field === field.fieldName)
        if (!fieldGroup) {
            fieldGroup = {
                translatedFieldName: field.translatedFieldName,
                field: field.fieldName,
                conditions: []
            }
            this.selectedFilter.filter.fieldGroups.push(fieldGroup)
        }
        const comparisonTypeDefault = field.allowedConditionsTypes[0]
        fieldGroup.conditions.push({
            value: '',
            valueTo: '',
            comparisonType: {
                conditionType: comparisonTypeDefault.type,
                comparatorType: comparisonTypeDefault.allowedComparatorTypes[0],
                additionalInfo: comparisonTypeDefault.additionalInfo
            },
            metaInfo: null,
            validationErrorCode: ScientificAnalysisConditionValidationErrorCode.NONE
        })
        this._onFilterModification()
    }

    getAllowedComparisonTypes(fieldName: string): ScientificAnalysisComparisonType[] {
        const entityField: ScientificAnalysisFieldDTO | undefined = this.state.entityFields.find(ef => ef.fieldName === fieldName)
        if (entityField) {
            return entityField.allowedConditionsTypes.map((conditionType: ScientificAnalysisConditionTypeDTO) => {
                return conditionType.allowedComparatorTypes.map((comparatorType: ScientificAnalysisComparatorType) => {
                    let vo: ScientificAnalysisComparisonType = {
                        comparatorType,
                        conditionType: conditionType.type,
                        additionalInfo: conditionType.additionalInfo
                    }
                    return vo
                })
            }).reduce((accumulator, ca) => {
                return accumulator.concat(ca)
            })
        }

        return []
    }

    getLocalizedComparisonName(comparison: ScientificAnalysisComparisonType): string {
        return this.$t(`scientificAnalysis.condition.${comparison.comparatorType}`) as string
    }

    onRemoveCondition(fieldGroup: ScientificAnalysisConditionFieldGroup, indexFieldGroup: number, indexCondition: number): void {
        fieldGroup.conditions.splice(indexCondition, 1)
        if (fieldGroup.conditions.length === 0) {
            this.selectedFilter.filter.fieldGroups.splice(indexFieldGroup, 1)
        }
        this._onFilterModification()
    }

    onRemoveAllConditions() {
        this.selectedFilter.filter.fieldGroups = []
    }

    onAddSameFieldCondition(fieldGroup: ScientificAnalysisConditionFieldGroup): void {
        const fieldFound = this.state.entityFields.find(field => field.fieldName === fieldGroup.field)
        if (fieldFound) { this.onFieldSelected(fieldFound) }
    }

    @Emit('searchModification')
    _onFilterModification():void { }

    @Emit('closeFilterPanelEvent')
    onCloseFilterPanel(): void { }

    get showEmptyFilterPanel(): boolean {
        return this.selectedFilter.filter.fieldGroups.length === 0
    }

    get isPatientFilter(): boolean {
        return this.selectedFilter.entityFilters.entityType === 'PATIENT'
    }

    get isCaseFilter(): boolean {
        return this.selectedFilter.entityFilters.entityType === 'CASE'
    }

    get isSampleFilter(): boolean {
        return this.selectedFilter.entityFilters.entityType === 'SAMPLE'
    }

    get isPortionFilter(): boolean {
        return this.selectedFilter.entityFilters.entityType === 'PORTION'
    }

    get isSubportionFilter(): boolean {
        return this.selectedFilter.entityFilters.entityType === 'SUBPORTION'
    }

    get isRequestFilter(): boolean {
        return this.selectedFilter.entityFilters.entityType === 'REQUEST'
    }

    get isComplementaryFilter(): boolean {
        return this.selectedFilter.entityFilters.entityType === 'COMPLEMENTARY'
    }
}
